import { Button } from "@/components/ui/button";
import {
    ArForm,
    ArNumberFormField,
    ArTextAreaFormField,
} from "@/v2/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FindOnePayslip from "@/v2/api/payslip/FindOne";
import { useNavigate } from "react-router-dom";
import { FreelancerPayslipFormSchema } from "@/v2/schema/payslip";
import { Employee } from "@/v2/types";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import { EmployeeComboBox } from "@/v2/components/ui/combo-box";
import CreateFreelancerPayslip from "@/v2/api/payslip/CreateFreelancer";
import UpdateFreelancerPayslip from "@/v2/api/payslip/UpdateFreelancer";
import { MonthSelectField } from "../MonthSelectField";
import { ArSwitchFormField } from "@/v2/components/ui/form/SwitchFormField";

export default function FreelancerPayslipForm({ id }: { id?: string }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Hooks must run unconditionally on every render, so this is declared
    // before any early `return` below (was previously after them).
    const [_emp, setEmp] = useState<Employee | null>(null);

    const query = useQuery({
        queryKey: ["payslips", id],
        queryFn: () => FindOnePayslip(id!),
        enabled: !!id,
    });

    const form = useForm<FreelancerPayslipFormSchema>({
        resolver: zodResolver(FreelancerPayslipFormSchema),
        defaultValues: {
            employee_id: "",
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            total_hours: 0,
            hourly_rate: 0,
            deduction: 0,
            net_amount: 0,
            eid_bonus: 0,
            food_allowance: 0,
            food_allowance_note: "",
            note: "",
            status: "DRAFT",
            type: "HOURLY",
            food_allowance_by_ar: false,
        },
    });

    const { reset } = form;

    // Watch every field that feeds into the net amount calculation
    const [
        total_hours,
        hourly_rate,
        food_allowance,
        deduction,
        bonus,
        food_allowance_by_AR,
    ] = useWatch({
        control: form.control,
        name: [
            "total_hours",
            "hourly_rate",
            "food_allowance",
            "deduction",
            "eid_bonus",
            "food_allowance_by_ar",
        ],
    });

    // Auto-calculate net amount: (hours * rate) + food allowance - deduction
    useEffect(() => {
        const total =
            (Number(total_hours) || 0) * (Number(hourly_rate) || 0) +
            (Number(food_allowance) || 0) +
            bonus -
            (Number(deduction) || 0);

        form.setValue("net_amount", total, {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [total_hours, hourly_rate, food_allowance, deduction, bonus, form]);

    useEffect(() => {
        if (query.data && !form.formState.isDirty) {
            reset({
                employee_id: query.data.employee_id ?? "",
                month: query.data.month ?? new Date().getMonth() + 1,
                year: query.data.year ?? new Date().getFullYear(),
                total_hours: query.data.total_hours ?? 0,
                hourly_rate: query.data.hourly_rate ?? 0,
                deduction: query.data.deduction ?? 0,
                net_amount: query.data.net_amount ?? 0,
                food_allowance: query?.data?.food_allowance ?? 0,
                eid_bonus: query?.data?.eid_bonus ?? 0,
                food_allowance_note: query.data.food_allowance_note ?? "",
                food_allowance_by_ar: query?.data.food_allowance_by_ar ?? false,
                note: query.data.note ?? "",
                status: "DRAFT",
                type: "HOURLY",
            });
        }
    }, [query.data, reset, form.formState.isDirty]);

    useEffect(() => {
        if (food_allowance_by_AR) {
            form.setValue("food_allowance", 0);
        }
    }, [food_allowance_by_AR]);

    const CreateMutation = useMutation({
        mutationFn: (data: FreelancerPayslipFormSchema) =>
            CreateFreelancerPayslip(data),
    });

    const EditMutation = useMutation({
        mutationFn: (data: FreelancerPayslipFormSchema) =>
            UpdateFreelancerPayslip({ id: id!, data }),
    });

    const handleFormSubmit = (data: FreelancerPayslipFormSchema) => {
        const mutation = id ? EditMutation : CreateMutation;

        const action = id ? "updated" : "created";

        const transformedData: FreelancerPayslipFormSchema = {
            ...data,
            status: "DRAFT",
            type: "HOURLY",
        };

        mutation.mutate(transformedData, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While ${id ? "updating" : "creating"} the payslip, we encountered an issue.`,
                });
            },
            onSuccess: (res) => {
                toast.success(`Payslip ${action} successfully`);
                navigate(`/payslips/freelancer/${res.id}`);
                queryClient.invalidateQueries({
                    queryKey: ["payslips"],
                });
            },
        });
    };

    if (id && query.isLoading) {
        return <LoadingAnimation />;
    }

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">Failed to load payslip</p>
                </div>
            </div>
        );
    }

    return (
        <ArForm
            form={form}
            formSchema={FreelancerPayslipFormSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Basic Information</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <EmployeeComboBox<FreelancerPayslipFormSchema>
                            name="employee_id"
                            defaultValue={query?.data?.employee ?? null}
                            setSelectedEmployee={setEmp}
                        />
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <MonthSelectField
                                control={form.control}
                                name="month"
                                label="Month"
                            />
                            <ArNumberFormField<FreelancerPayslipFormSchema>
                                label="Year"
                                name="year"
                                placeholder="Enter year"
                            />
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Financial Details</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArNumberFormField<FreelancerPayslipFormSchema>
                            label="Total Hours"
                            name="total_hours"
                            placeholder="Enter total hours"
                        />
                        <ArNumberFormField<FreelancerPayslipFormSchema>
                            label="Hourly Rate"
                            name="hourly_rate"
                            placeholder="Enter hourly rate"
                        />

                        <ArNumberFormField<FreelancerPayslipFormSchema>
                            label="Bonus"
                            name="eid_bonus"
                            placeholder="Enter bonus amount"
                        />

                        <ArNumberFormField<FreelancerPayslipFormSchema>
                            label="Deduction"
                            name="deduction"
                            placeholder="Enter deduction"
                        />
                        <ArNumberFormField<FreelancerPayslipFormSchema>
                            label="Food Allowance"
                            name="food_allowance"
                            placeholder="Enter food allowance"
                            disabled={food_allowance_by_AR}
                        />
                        <ArSwitchFormField<FreelancerPayslipFormSchema>
                            name="food_allowance_by_ar"
                            label="Food Allowance By AR"
                        />
                    </div>
                </div>

                {/* Totals */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Totals</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArNumberFormField<FreelancerPayslipFormSchema>
                            label="Amount"
                            name="net_amount"
                            placeholder="Enter amount"
                            disabled
                        />
                    </div>
                </div>

                {/* Notes */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Notes</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArTextAreaFormField<FreelancerPayslipFormSchema>
                            label="Food Allowance Note"
                            name="food_allowance_note"
                            placeholder="Enter food allowance note"
                        />
                        <ArTextAreaFormField<FreelancerPayslipFormSchema>
                            label="Note"
                            name="note"
                            placeholder="Enter note"
                        />
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                                ? "Update Payslip"
                                : "Create Payslip"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
